import { VID_DOCUMENT as VID_META, TORQUE_TABLE as TORQUE_BY_KEY, JOINTING_NOTES } from './vidTorque';
import { VID_TYPES, VID_SCHEDULES } from './vidValveData';
import { padTypeCode } from './vidHelpers';

const STATUS_COPY = {
  dummy: {
    tone: 'stop',
    title: 'Dummy type code — do not use this schedule',
    body: 'Type codes 500–599 are dummy family codes. VID 0.1.3.2: only a real type code may be used for valving and enhanced test. Identify the vessel against the identity drawing before fitting valves.',
  },
  obsolete: {
    tone: 'stop',
    title: 'Obsolete type — do not revalve to return to service',
    body: 'This type is listed as obsolete in VID Issue L. Scrap when returned to the depot. Do not fit a current valve set against this code.',
  },
  identify: {
    tone: 'stop',
    title: 'Type must be identified before valving',
    body: 'VID requires this vessel to be identified (or it is a Competition Commission transfer without a confirmed identity drawing). Do not select valves from a guessed type code.',
  },
  refer: {
    tone: 'stop',
    title: 'Contact Calor National Engineering',
    body: 'The VID schedule for this type is “Refer to National Engineering”. No valve list is shown so a depot list cannot be used in error. Open the Calor Line of Contact page for National Engineering.',
    contactNationalEngineering: true,
  },
  unknown: {
    tone: 'stop',
    title: 'Type code not in VID Issue L',
    body: 'This code is not on the Issue L type index. Do not guess a nearby code. Confirm the nameplate and identity drawing, then check the paper VID.',
  },
  no_schedule: {
    tone: 'stop',
    title: 'No valving schedule table for this type',
    body: 'The type is indexed in VID Issue L but has no valving-schedule row that can be shown without guessing. Open the identity drawing in the VID. Section 5 (over 4000 L) types are drawing-led, not table-led.',
  },
};

const attachTorque = (rows = []) =>
  (rows || [])
    .filter(Boolean)
    .map((row) => {
      const torque = row.torqueKey ? TORQUE_BY_KEY[row.torqueKey] || null : null;
      return { ...row, torque };
    });

export const lookupValveSettings = (rawCode) => {
  const code = padTypeCode(rawCode);
  const source = {
      document: VID_META.title,
      issue: VID_META.issue,
      date: VID_META.date,
      torquePage: VID_META.torquePage,
    pages: [],
  };

  if (!code) {
    return {
      ok: false,
      code: '',
      tone: 'empty',
      title: 'Enter the type code first',
      body: 'Type codes are three digits (for example 019 or 372). The valve list is not shown until a code is entered.',
      source,
      type: null,
      fitNow: null,
      existing: null,
      notes: JOINTING_NOTES,
    };
  }

  if (Number(code) >= 500 && Number(code) <= 599) {
    const t = VID_TYPES[code] || {
      code,
      name: 'Dummy type code',
      status: 'dummy',
      alternatives: [],
      indexPage: 5,
    };
    return {
      ok: false,
      code,
      ...STATUS_COPY.dummy,
      source: { ...source, pages: [t.indexPage, VID_META.torquePage] },
      type: t,
      fitNow: null,
      existing: null,
      notes: JOINTING_NOTES,
    };
  }

  const type = VID_TYPES[code];
  if (!type) {
    return {
      ok: false,
      code,
      ...STATUS_COPY.unknown,
      source,
      type: null,
      fitNow: null,
      existing: null,
      notes: JOINTING_NOTES,
    };
  }

  const blocked = ['obsolete', 'identify', 'refer', 'dummy'].includes(type.status);
  if (blocked) {
    const copy = STATUS_COPY[type.status];
    return {
      ok: false,
      code,
      ...copy,
      source: { ...source, pages: [type.indexPage, VID_META.torquePage].filter(Boolean) },
      type,
      fitNow: null,
      existing: null,
      notes: JOINTING_NOTES,
    };
  }

  const schedule = VID_SCHEDULES[code];
  if (!schedule || (!schedule.new && !schedule.existing)) {
    return {
      ok: false,
      code,
      ...STATUS_COPY.no_schedule,
      source: { ...source, pages: [type.indexPage, VID_META.torquePage].filter(Boolean) },
      type,
      fitNow: null,
      existing: null,
      notes: JOINTING_NOTES,
    };
  }

  if (schedule.obsoleteOnly && !schedule.new) {
    return {
      ok: false,
      code,
      ...STATUS_COPY.obsolete,
      source: {
        ...source,
        pages: [type.indexPage, schedule.pages?.existing, VID_META.torquePage].filter(Boolean),
      },
      type,
      fitNow: null,
      existing: null,
      notes: JOINTING_NOTES,
    };
  }

  const pages = [
    type.indexPage,
    schedule.pages?.new,
    schedule.pages?.existing,
    VID_META.torquePage,
  ].filter(Boolean);

  const fitNow = schedule.new
    ? {
        label: 'Fit these now (NEW / current maintenance schedule)',
        rows: attachTorque(schedule.new),
      }
    : null;

  const existing = schedule.existing
    ? {
        label: schedule.new
          ? 'Existing (pre-OPD) schedule — identification only, not the current fit list'
          : 'VID existing schedule (no NEW row for this type — confirm against the VID page before fitting)',
        rows: attachTorque(schedule.existing),
      }
    : null;

  return {
    ok: true,
    code,
    tone: schedule.new ? 'ok' : 'caution',
    title: schedule.new
      ? `Valve set for type ${code}`
      : `Type ${code} — existing schedule only`,
    body: schedule.new
      ? 'Current maintenance valves from the VID NEW schedule, with Section 10 torques. This is a read-only aid — it does not write onto the certificate.'
      : 'There is no NEW schedule row for this type. The existing schedule is shown. Confirm the paper VID before fitting.',
    source: { ...source, pages: [...new Set(pages)] },
    type,
    layout: schedule.layout,
    fitNow,
    existing,
    notes: JOINTING_NOTES,
  };
};
